import * as ArchiveConstants from 'archive/ArchiveConstants';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {Concentration, LegacyMeasurement} from 'constants/MeasurementConstants';
import ModelTypeConstants, {StorableItemType} from 'constants/ModelTypeConstants';
import {TagSchema, TagSchemaSummary} from 'constants/RegistryConstants';
import {
  CheckOutStatusInfo,
  SampleRestrictionStatus,
  SampleRoles,
  SampleStatusItem,
} from 'constants/SampleStatusConstants';
import {User} from 'constants/UserConstants';
import * as FileConstants from 'folder-items/FileConstants';
import {AsyncValidationStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {EvaluatedCellError} from 'notebook/tables/excess/EvaluationConstants';
import {ExperimentalRole} from 'samples/experimental-roles/ExperimentalRoleConstants';
import {
  ContainerSchema,
  GridSchema,
  PlateSchema,
  StorableSchema,
  StorableSchemaSummary,
} from 'samples/storables/StorableSchemaConstants';
import {SchemaFieldEdge} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import {SchemaValidationErrorRecord, SchemaValidationState} from 'schemas/SchemaValidationConstants';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {Optional} from 'util/Types';

import {BoxGraphQL} from '../../graphql-fragments/inventory/__generated/BoxGraphQL';
import {BoxSummaryGraphQL} from '../../graphql-fragments/inventory/__generated/BoxSummaryGraphQL';
import {ContainerGraphQL} from '../../graphql-fragments/inventory/__generated/ContainerGraphQL';
import {GridGraphQL} from '../../graphql-fragments/inventory/__generated/GridGraphQL';
import {LocationSummaryGraphQL} from '../../graphql-fragments/inventory/__generated/LocationSummaryGraphQL';
import {PlateGraphQL} from '../../graphql-fragments/inventory/__generated/PlateGraphQL';
import {PlateSummaryGraphQL} from '../../graphql-fragments/inventory/__generated/PlateSummaryGraphQL';
import {StorableSummaryGraphQL} from '../../graphql-fragments/inventory/__generated/StorableSummaryGraphQL';
import {SourceSummary} from './SourceConstants';

type Tag = FileConstants.Tag;

// Keep in sync with `benchling/inventory/inventory_util.py`
export const MAX_SIGNIFICANT_FIGURES = 6;

export function getMaxSignificantFigures(): number {
  return BenchlingConfig.INVENTORY_ENABLE_HIGHER_PRECISION ? 15 : 6;
}

export interface BatchViewState {
  view: BatchViewType;
  batchId: string | null;
  containerId?: string | null;
  filter: string;
  createBatchModal: null;
}

export enum BatchViewType {
  VIEW_ALL = 'VIEW_ALL',
  VIEW = 'VIEW',
}

export const MODAL_PANE = {
  CHOOSE_TRANSFER_INPUT: 'CHOOSE_TRANSFER_INPUT',
  CHOOSE_TRANSFER_OUTPUT: 'CHOOSE_TRANSFER_OUTPUT',
  CONFIRM_CONCENTRATIONS: 'CONFIRM_CONCENTRATIONS',
  CONFIRM_SAMPLE_CONTROLS: 'CONFIRM_SAMPLE_CONTROLS',
  CONFIRM_TRANSFER_QUANTITIES: 'CONFIRM_TRANSFER_QUANTITIES',
  CREATE_BOX: 'CREATE_BOX',
  CREATE_CONTAINERS: 'CREATE_CONTAINERS',
  CREATE_LOCATION: 'CREATE_LOCATION',
  CREATE_PLATE: 'CREATE_PLATE',
  EXISTING_PLATE: 'EXISTING_PLATE',
  SELECT_PARENT_STORABLE: 'SELECT_PARENT_STORABLE',
  TRANSFER_PLATES_QUANTITY: 'TRANSFER_PLATES_QUANTITY',
};

export enum ModalPane {
  CHOOSE_TRANSFER_INPUT = 'CHOOSE_TRANSFER_INPUT',
  CHOOSE_TRANSFER_OUTPUT = 'CHOOSE_TRANSFER_OUTPUT',
  CONFIRM_CONCENTRATIONS = 'CONFIRM_CONCENTRATIONS',
  CONFIRM_SAMPLE_CONTROLS = 'CONFIRM_SAMPLE_CONTROLS',
  CONFIRM_TRANSFER_QUANTITIES = 'CONFIRM_TRANSFER_QUANTITIES',
  CREATE_BOX = 'CREATE_BOX',
  CREATE_CONTAINERS = 'CREATE_CONTAINERS',
  CREATE_LOCATION = 'CREATE_LOCATION',
  CREATE_PLATE = 'CREATE_PLATE',
  EXISTING_PLATE = 'EXISTING_PLATE',
  SELECT_PARENT_STORABLE = 'SELECT_PARENT_STORABLE',
  TRANSFER_PLATES_QUANTITY = 'TRANSFER_PLATES_QUANTITY',
}

export enum TransferFlow {
  TRANSFER_IN = 'TRANSFER_IN',
  TRANSFER_OUT = 'TRANSFER_OUT',
  TRANSFER_GLOBAL = 'TRANSFER_GLOBAL',
}

export const PARENT_STORABLE_PANES = [
  MODAL_PANE.CREATE_BOX,
  MODAL_PANE.CREATE_CONTAINERS,
  MODAL_PANE.CREATE_LOCATION,
  MODAL_PANE.CREATE_PLATE,
  MODAL_PANE.SELECT_PARENT_STORABLE,
];

export type CreateStorablePanes =
  | ModalPane.CREATE_BOX
  | ModalPane.CREATE_CONTAINERS
  | ModalPane.CREATE_LOCATION
  | ModalPane.CREATE_PLATE;

// Keep in sync with SpreadsheetFormat enum in benchling/inventory/storables/grids_util.py
export const UPLOAD_CONTAINERS_SPREADSHEET_FORMAT = {
  ONE_CONTAINER_PER_ROW: 'ONE_CONTAINER_PER_ROW',
  PLATE_MAP: 'PLATE_MAP',
};

export const PLATE_MAP_RAW_TEXT_NAME = 'plate_map_raw_text.csv';

export const TRANSFER_MODAL_PANES = [
  MODAL_PANE.CHOOSE_TRANSFER_INPUT,
  MODAL_PANE.CHOOSE_TRANSFER_OUTPUT,
  MODAL_PANE.CONFIRM_TRANSFER_QUANTITIES,
  MODAL_PANE.CONFIRM_CONCENTRATIONS,
  MODAL_PANE.CONFIRM_SAMPLE_CONTROLS,
];

export type TransferModalPane =
  | ModalPane.CHOOSE_TRANSFER_INPUT
  | ModalPane.CHOOSE_TRANSFER_OUTPUT
  | ModalPane.CONFIRM_TRANSFER_QUANTITIES
  | ModalPane.CONFIRM_CONCENTRATIONS
  | ModalPane.CONFIRM_SAMPLE_CONTROLS;

export const MODAL_PANE_TO_TITLE = {
  [MODAL_PANE.CONFIRM_CONCENTRATIONS]: 'Confirm concentrations',
  [MODAL_PANE.CREATE_BOX]: 'Create box',
  [MODAL_PANE.CREATE_CONTAINERS]: 'Create containers',
  [MODAL_PANE.CREATE_LOCATION]: 'Create location',
  [MODAL_PANE.CREATE_PLATE]: 'Create plate',
  [MODAL_PANE.EXISTING_PLATE]: 'Find existing plate',
};

export interface SampleFile {
  id: string;
  iconId: string;
  name: string;
  result?: object; // Deprecated and does not exist on any deploys
  tags: Array<Tag>;
  tagSchema: TagSchema;
  residues?: string | null;
  schemaValidationState?: SchemaValidationState | null;
  url: string;
}

// Keep in sync with batch.py to_dict(include_file=True)
export interface Batch {
  archiveRecord: ArchiveConstants.ArchiveRecord | null;
  batchSchemaId: string;
  concentration: Concentration;
  createdAt: string;
  creator: User;
  file: SampleFile;
  id: string;
  migratedToBasicFolderItemId: string | null;
  modifiedAt: string;
  name: string;
  permissions: Permissions;
  tags: Array<FileConstants.SchemaTag>;
  url: string;
}

export interface BatchSummary {
  id: string;
  name: string;
}

export interface ContainerContent {
  id: number;
  // Deprecated: use containedItemId instead.
  batchId?: string | null;
  containedItemId: string;
  containedItemTagSchemaId: string;
  containerId: string;
  concentration: Concentration;
}

export type ContainableSummary = Batch | FileConstants.BioentitySummary;

export function tagSchemaHasNoContainableType(schema: TagSchema): boolean {
  return !schema.isContainable && schema.batchSchemaId == null;
}

export interface BatchLinkField {
  id: number;
  fieldType: string;
  name?: string;
  requiredLink: {
    batchSchema?: BatchSchema;
    batchSchemaGraph?: object;
  };
}

export enum BatchSchemaNamingScheme {
  BIOENTITY_NAME = 'BIOENTITY_NAME',
  BIOENTITY_REGISTRY_ID = 'BIOENTITY_REGISTRY_ID',
  BIOENTITY_REGISTRY_ID_WITH_DATE = 'BIOENTITY_REGISTRY_ID_WITH_DATE',
}

// Keep in sync with `BatchSchema.NamingScheme` in `batch_schema.py`
export const BATCH_SCHEMA_NAMING_SCHEMES = {
  BIOENTITY_REGISTRY_ID: 'BIOENTITY_REGISTRY_ID',
  BIOENTITY_REGISTRY_ID_WITH_DATE: 'BIOENTITY_REGISTRY_ID_WITH_DATE',
  BIOENTITY_NAME: 'BIOENTITY_NAME',
};

export interface BatchSchema extends Schema {
  fileTypeName: string;
  namingScheme: BatchSchemaNamingScheme;
  sqlIdentifier: string;
  tagSchemaId: string;
  hidden: boolean;
}

interface BioentitySchemaBatchLinkProps {
  entityToBatchLink?: boolean;
}

export interface BatchSchemaBatchLinkProps {
  pathsByTargetBatch: {[batchSchemaId: string]: Array<Array<SchemaFieldEdge>>};
  targetBatchSchemasById: {[batchSchemaId: string]: BatchSchema};
  sourceTagSchemaId: string;
}

export type BatchLinkProps = BioentitySchemaBatchLinkProps | BatchSchemaBatchLinkProps;

export interface BatchSchemaFilters {
  options: Array<BatchSchema>;
  value?: BatchSchema;
}

export interface ParentStorableData {
  // nullable depending on ENABLE_REQUIRED_PARENT_STORABLE and whether the storables are locations
  id: string | null;
  gridCellColumnIndex: number | null;
  gridCellRowIndex: number | null;
}

export interface StorableSummary {
  archiveRecord: ArchiveConstants.ArchiveRecord | null;
  barcode: Optional<string>;
  checkOutStatusInfo?: CheckOutStatusInfo;
  createdAt: string;
  modifiedAt: string;
  id: string;
  name: string;
  parentStorableId?: Optional<string>;
  restrictionStatus?: SampleRestrictionStatus;
  sampleRoles?: SampleRoles;
  source: SourceSummary;
  starred: boolean;
  storableSchema: StorableSchema;
  tags: Array<FileConstants.SchemaTag>;
}

export interface MinimalStorableSummaryWithSchemaId extends Pick<StorableSummary, 'id' | 'name'> {
  storableSchema: Pick<StorableSchema, 'id'>;
}

export interface ValidationStateInfo {
  id: string;
  validationState?: {
    status: AsyncValidationStatus;
    validationErrorRecords: Array<SchemaValidationErrorRecord> | null;
  } | null;
}

export interface StorableWithPermissions extends StorableSummary {
  creator: User;
  permissions: Permissions;
}

export interface Storable extends StorableWithPermissions {
  sourceId: string;
}

export type TransferContainerSummary = ContainerSummary & {gridCellCoordinates?: string | null};

export type InitialParentStorable = BoxSummaryGraphQL | PlateSummaryGraphQL | LocationSummaryGraphQL;

export interface Location extends Storable {
  url: string;
}
export interface LocationSummary extends StorableSummary {
  url: string;
}

export interface LocationAncestry extends LocationSummary {
  parent: LocationAncestry | null;
}

export interface ListedLocation extends Location {
  parent: LocationAncestry | null;
}

export interface Grid extends Storable {
  cells: Array<Array<GridCell | null>>;
  storableSchema: GridSchema;
  // Reported for plates only, and absent from REST payloads that don't include it.
  missingRequiredFields?: Array<MissingRequiredField>;
}

/**
 * Grid type for when all cells are known to be accessible.
 *
 * Applicable for well plates, since their wells don't have separate permissions, or after another grid has
 * been checked with `isFullyAccessibleGrid`. Useful for when all cells will be acted on at once.
 */
export interface FullyAccessibleGrid extends Storable {
  cells: Array<Array<AccessibleGridCell | null>>;
  // Reported for plates only, and absent from REST payloads that don't include it.
  missingRequiredFields?: Array<MissingRequiredField>;
  storableSchema: GridSchema;
}

export interface FullyAccessiblePlateGraphQL extends PlateGraphQL {
  cells: Array<Array<AccessibleGridCellGraphQL | null>>;
}

export interface FullyAccessibleBoxGraphQL extends BoxGraphQL {
  cells: Array<Array<AccessibleGridCellGraphQL | null>>;
}

export type FullyAccessibleGridGraphQL = FullyAccessiblePlateGraphQL | FullyAccessibleBoxGraphQL;

export function isFullyAccessibleGrid(grid?: Grid | null): grid is FullyAccessibleGrid {
  return (
    grid != null &&
    grid.cells.every((cellRow) => cellRow.every((cell) => cell == null || isAccessibleGridCell(cell)))
  );
}

export function isFullyAccessibleGridGraphQL(grid?: GridGraphQL | null): grid is FullyAccessibleGridGraphQL {
  return (
    grid != null &&
    grid.cells.every((cellRow) => cellRow.every((cell) => cell == null || isAccessibleGridCellGraphQL(cell)))
  );
}

export interface GridWithMinimalCells extends Storable {
  cells: Array<Array<MinimalContainerGridCell | null>>;
  storableSchema: GridSchema;
}

export interface Plate extends Grid {
  storableSchema: PlateSchema;
}

export type Box = Grid;

export interface GridCellOverride {
  innerClassName?: string;
  tooltipTitle?: string;
  dataTestAttrs?: DataTestAttrs;
}

export interface GridSummary extends StorableSummary {
  storableSchema: GridSchema;
}

export type GridSummaryGraphQL = PlateSummaryGraphQL | BoxSummaryGraphQL;

export interface GridCellSummary {
  columnIndex: number;
  rowIndex: number;
}

// A required schema field a storable has no value for. `id` matches `tagSchemaField.id` on its tags.
export interface MissingRequiredField {
  id: number;
  displayName: string;
}

export interface ContainerSummary extends StorableSummary {
  url: string;
  volume?: LegacyMeasurement;
  quantity: LegacyMeasurement;
  restrictionStatus: SampleRestrictionStatus;
  isFixedPlateWell: boolean;
  role: ExperimentalRole | null;
  // Absent from some REST payloads that don't include it.
  missingRequiredFields?: Array<MissingRequiredField>;
}

export interface Container extends Storable, ContainerSummary {
  // TODO(T8705): Batch->Container relationship is deprecated.
  batch?: Batch;
  contents: Array<ContainerContent>;
  containerSchemaId: string;
  creator: User;
  gridCell?: GridCellSummary;
  restrictionStatus: SampleRestrictionStatus;
  storableSchema: ContainerSchema;
}
export type GridCellOrEmptyGridCell = GridCell | EmptyGridCell;
export interface EmptyGridCell extends GridCellSummary {
  container: null;
}

export interface InaccessibleStorable {
  invalidId: string;
}
export function isInaccessibleStorable(
  storable: InaccessibleStorable | SampleStatusItem
): storable is InaccessibleStorable {
  return (storable as InaccessibleStorable).invalidId != null;
}

export function getAccessibleOrInaccessibleStorableId(
  storable: InaccessibleStorable | SampleStatusItem
): string {
  return isInaccessibleStorable(storable) ? storable.invalidId : storable.id;
}
// Minimal types needed for rendering containers in a grid -- we want sample status info to check for errors
// and barcode/contents/id for basic rendering
export type MinimalContainer = SampleStatusItem & {
  barcode: Optional<string>;
  contents: Array<{containedItemId: string}>;
};
export interface MinimalContainerGridCell extends GridCellSummary {
  container: MinimalContainer | InaccessibleStorable;
}

export interface GridCell extends GridCellSummary {
  container: Container | InaccessibleStorable;
}

export interface AccessibleGridCell extends GridCellSummary {
  container: Container;
}

type GridCellGraphQL = NonNullable<GridGraphQL['cells'][number][number]>;

export type AccessibleGridCellGraphQL = GridCellGraphQL & {
  container: ContainerGraphQL;
};

export function isAccessibleGridCell(gridCell?: GridCellSummary | null): gridCell is AccessibleGridCell {
  return (
    (gridCell as GridCell)?.container != null && !isInaccessibleStorable((gridCell as GridCell).container)
  );
}

export function isAccessibleGridCellGraphQL(
  gridCell?: GridCellGraphQL | null
): gridCell is AccessibleGridCellGraphQL {
  return gridCell != null && !isInaccessible(gridCell.container);
}

export interface TextInfo {
  id: string;
  name: string | null;
  value: string | null;
}

export interface NumberInfo {
  id: string;
  name: string | null;
  isFloat: boolean;
  value: number | null;
}

export interface BooleanInfo {
  id: string;
  name: string | null;
  value: boolean | null;
}

export interface ContainerInfo {
  id: string;
  name: string;
  barcode?: Optional<string>;
  gridCell?: GridCellSummary | null;
}

export interface GridInfo {
  id: string;
  name: string;
  barcode: Optional<string>;
}

export interface BatchDescendantCounts {
  numNonWellContainers: number;
  numPlates: number;
}

export interface BatchDescendants {
  nonWellContainers: Array<ContainerSummary>;
  plates: Array<GridSummary>;
}

export interface SelectedParentStorable {
  storable: StorableSummaryGraphQL | null;
  gridCells: Array<GridCellSummary>;
}

export enum SelectGridMode {
  EXISTING = 'EXISTING',
  CREATE = 'CREATE',
  CHOOSE = 'CHOOSE',
}

export interface ContainerTransferInfo {
  inputStorableIds: Array<string>;
  outputStorableIds: Array<string>;
}

export interface HydratedContainerTransferInfo {
  inputStorables: Array<StorableSummary | null>;
  outputStorables: Array<StorableSummary | null>;
}

export interface ContainerTransfer {
  id?: string;
  inputSampleId: string | null;
  outputContainer: ContainerSummary;
  volume?: LegacyMeasurement;
  quantity: LegacyMeasurement;
}

export interface ResponseContainerTransfer extends ContainerTransfer {
  inputContainer: ContainerSummary | null;
}

export interface LabelTemplate {
  id: string;
  name: string;
  registryId: string;
  zplTemplate: string;
}

export interface Printer {
  id: string;
  name: string;
  address: string;
  port?: number;
  description?: string;
  registryId: string;
}

export type PrinterForCreate = Omit<Printer, 'id' | 'registryId'>;

export interface PrinterServerResponse {
  printer: Printer;
}

export interface ValidationErrors {
  barcodeError?: string;
  concentrationError?: string;
  containerSchemaError?: string;
  contentsError?: string;
  formError?: string;
  numToCreateError?: string;
  parentStorableError?: string;
  parentStorableGridError?: string;
  projectError?: string;
  quantityError?: string;
  schemaError?: string;
  tagsError?: string;
  // Error message is already handled by the capacity input; simply need to block creation
  hasLocationCapacityError?: boolean;
}

export interface ChildContainerStatus {
  numFilledContainers: number;
  numTotalContainers: number;
}

export interface ChildStorableSchemasByLocationId {
  [locationId: string]: Array<{
    childStorableSchema: StorableSchemaSummary;
    numChildStorables: number;
  }>;
}

export interface LocationCapacityInfo {
  allowsNonLocationChildren: boolean;
  occupiedCapacity: number | null;
  totalCapacity: number | null;
  validChildSchemaIds: Array<string> | null;
}

export interface PlateSummaryExtras {
  viewableWellCountByPlateId: {[plateId: string]: number};
}

// TODO(TypeScript): This is initialized to {}, but aside from that, all properties are present.
// It should probably be changed to be null/optional and have all fields required.
export interface StorableSummaryExtras {
  ancestralStorablesById?: {[storableId: string]: Array<StorableSummary>}; // Deprecated
  ancestralStorableIdsByStorableId?: {[storableId: string]: Array<string>};
  childContainerParentFileSchemasByGridId?: {[gridId: string]: Array<TagSchemaSummary>};
  childContainerStatusesByGridId?: {
    [gridId: string]: ChildContainerStatus;
  };
  childStorableSchemasByLocationId?: ChildStorableSchemasByLocationId;
  gridCellsByContainerId?: {[containerId: string]: GridCellSummary | null};
  containedItemsByContainerId?: {[containerId: string]: Array<ContainableSummary>};
  containableConcentrationsByContainerId?: {
    [containerId: string]: {[containableId: string]: LegacyMeasurement};
  };
  locationCapacityById?: {
    [locationId: string]: LocationCapacityInfo;
  };
}

export const GRID_SCHEMA_CLASSES = [ModelTypeConstants.BOX_SCHEMA, ModelTypeConstants.PLATE_SCHEMA];

export enum GridSchemaClass {
  BOX_SCHEMA = ModelTypeConstants.BOX_SCHEMA,
  PLATE_SCHEMA = ModelTypeConstants.PLATE_SCHEMA,
}

export const GRID_SCHEMA_CLASS_TO_TYPE_TEXT = {
  [ModelTypeConstants.BOX_SCHEMA]: 'Box',
  [ModelTypeConstants.PLATE_SCHEMA]: 'Plate',
};

export enum GridType {
  BOX = 'box',
  PLATE = 'plate',
}

export const GRID_CLASSES: Array<GridType> = [GridType.BOX, GridType.PLATE];

export const STORABLE_TYPES = [
  ModelTypeConstants.CONTAINER,
  ModelTypeConstants.BOX,
  ModelTypeConstants.PLATE,
  ModelTypeConstants.LOCATION,
] as const;

export const STORABLE_SCHEMA_TYPES = [
  ModelTypeConstants.CONTAINER_SCHEMA,
  ModelTypeConstants.BOX_SCHEMA,
  ModelTypeConstants.PLATE_SCHEMA,
  ModelTypeConstants.LOCATION_SCHEMA,
] as const;

export const STORABLE_SCHEMA_TYPE_TO_STORABLE_TYPE: {[key: string]: string} = {
  [ModelTypeConstants.BOX_SCHEMA]: ModelTypeConstants.BOX,
  [ModelTypeConstants.CONTAINER_SCHEMA]: ModelTypeConstants.CONTAINER,
  [ModelTypeConstants.LOCATION_SCHEMA]: ModelTypeConstants.LOCATION,
  [ModelTypeConstants.PLATE_SCHEMA]: ModelTypeConstants.PLATE,
};

// Keep in sync with `Storable.valid_parent_storable_classes`
export const STORABLE_CLASS_TO_VALID_PARENT_CLASSES: {
  [key: string]: Array<StorableItemType>;
} = {
  [ModelTypeConstants.CONTAINER]: [
    ModelTypeConstants.BOX,
    ModelTypeConstants.PLATE,
    ModelTypeConstants.LOCATION,
  ],
  [ModelTypeConstants.BOX]: [ModelTypeConstants.LOCATION],
  [ModelTypeConstants.PLATE]: [ModelTypeConstants.LOCATION],
  [ModelTypeConstants.LOCATION]: [ModelTypeConstants.LOCATION],
};

// This is a "normal" sample -- e.g., a batch or bioentity or container

export interface BaseSampleDetails {
  // batchInfo and bioentityInfo both null means that the user doesn't have permissions on the sample
  batchInfo: FileConstants.BatchInfo | null;
  bioentityInfo: FileConstants.BioentityInfo | null;
  booleanInfo: BooleanInfo | null;
  boxInfo: GridInfo | null;
  containerInfo: ContainerInfo | null;
  numberInfo: NumberInfo | null;
  plateInfo: GridInfo | null;
  textInfo: TextInfo | null;
  error: Optional<EvaluatedCellError>;
}

// Sets all values of BaseSampleDetails to null except for K, which is non-nullable, and 'error' which is either null or an EvaluatedCellError
type SpecificSampleDetails<K extends keyof BaseSampleDetails> = {
  [P in keyof BaseSampleDetails]: P extends K | 'error'
    ? P extends K
      ? NonNullable<BaseSampleDetails[P]>
      : BaseSampleDetails[P]
    : null;
};

type BioentitySampleDetails = SpecificSampleDetails<'bioentityInfo'>;
type ContainerSampleDetails = SpecificSampleDetails<'containerInfo'>;
type NumberSampleDetails = SpecificSampleDetails<'numberInfo'>;
type TextSampleDetails = SpecificSampleDetails<'textInfo'>;
type BooleanSampleDetails = SpecificSampleDetails<'booleanInfo'>;

// This version of SampleDetails represents a plate as a whole. It is used when propagating plates
// across runs or tables
type PlateSampleDetails = SpecificSampleDetails<'plateInfo'>;

type BoxSampleDetails = SpecificSampleDetails<'boxInfo'>;

// This version of SampleDetails represents a WELL, which is {plate!, position!}
export interface WellSampleDetails extends PlateSampleDetails {
  /**
   * We use a string to represent the position when we don't have details about the plate grid size,
   * which would require an asynchronous GraphQL call.
   */
  position: GridCellSummary | string;
}

export function isBioentitySample(sample: SampleDetails): sample is BioentitySampleDetails {
  return sample.bioentityInfo != null;
}

export function isContainerSample(sample: SampleDetails): sample is ContainerSampleDetails {
  return sample.containerInfo != null;
}

export function isWellSample(sample: SampleDetails): sample is WellSampleDetails {
  return (sample as WellSampleDetails).position != null;
}

export type SampleDetails =
  | BaseSampleDetails
  | BoxSampleDetails
  | PlateSampleDetails
  | WellSampleDetails
  | TextSampleDetails
  | NumberSampleDetails
  | BooleanSampleDetails;

interface WithSchemaExtension {
  schema: TagSchema | BatchSchema | null;
}

export type SampleDetailsWithSchema = SampleDetails & WithSchemaExtension;
export interface StorableModalCreateProps {
  name: string;
  barcode: string;
  selectedSchemaId: string;
  sourceId: string;
  parentStorableData: ParentStorableData;
  tags: Array<Tag>;
}

interface PartialContainerContent {
  __typename: 'ContainerContentNode';
  containedItem: {__typename: string};
}

export function getNonBatchContainerContents<
  T extends PartialContainerContent,
  R extends PartialContainerContent,
>(contents: Array<T>): Array<R> {
  return contents.filter(
    (content) => content.containedItem.__typename !== 'BatchNode'
  ) as unknown as Array<R>;
}

export function getAccessibleNonBatchContainerContents<
  T extends PartialContainerContent,
  R extends PartialContainerContent,
>(contents: Array<T>): Array<R> {
  return contents.filter(
    (content) =>
      content.containedItem.__typename !== 'BatchNode' &&
      content.containedItem.__typename !== 'InaccessibleInfoNode'
  ) as unknown as Array<R>;
}
